import InsightCard from '@components/charts/membership/revenue/InsightCard'
import {
  figureValue,
  formatInteger,
  formatPercent,
} from '@components/charts/membership/revenue/figures'
import SectionHead from '@components/charts/membership/revenue/SectionHead'
import useRevenueLinks from '@components/charts/membership/revenue/useRevenueLinks'
import { ResponseMembershipAttentionAnalyticsStatistics } from '@models/membership/MembershipAnalyticsStatistics'
import { analyticsCurrency } from '@utils/currencyFormatter'
import dayjs from 'dayjs'
import { Col, Flex, Row, Typography } from 'antd'

const { Text } = Typography

const join = (...parts: (string | false | undefined)[]) =>
  parts.filter(Boolean).join(' · ') || undefined

/**
 * "Needs attention" (Figma 1021:2501): four insight cards in a 2x2 grid. A card
 * whose count is 0 turns into a green "all good" state with no link.
 */
interface Props {
  /** Fetched once by the page: the Upcoming renewals block reads its currency. */
  attention: {
    data?: ResponseMembershipAttentionAnalyticsStatistics
    isLoading: boolean
    error?: string
  }
}

const NeedsAttention = ({ attention }: Props) => {
  const { data, isLoading, error } = attention
  const link = useRevenueLinks()

  const money = (cents?: number | null) =>
    analyticsCurrency(cents, false, data?.currency)
  const hasAmount = (cents?: number | null): cents is number =>
    typeof cents === 'number'

  const today = dayjs().format('YYYY-MM-DD')
  const in30 = dayjs().add(30, 'day').format('YYYY-MM-DD')
  // Oldest unrecovered failure to today; a year back when the API has no date.
  // The renewals list rejects ranges over 3 years: keep the most recent
  // failures by moving the start, never the end.
  const earliestStart = dayjs().subtract(3, 'year').add(1, 'day')
  const oldestDue = data?.failedRenewals?.oldestDueDate
  const failedFrom = (
    oldestDue
      ? dayjs(oldestDue).isBefore(earliestStart)
        ? earliestStart
        : dayjs(oldestDue)
      : dayjs().subtract(365, 'day')
  ).format('YYYY-MM-DD')

  const failed = data?.failedRenewals
  const stuck = data?.stuckReviews
  const missing = data?.withoutPaymentMethod
  const dormant = data?.dormantRenewals
  const cardFailureRate = figureValue(data?.cardFailureRate90d)

  const loaded = !!data

  return (
    <Flex vertical gap={24}>
      <SectionHead title="Needs attention" />
      <Row gutter={[24, 24]} align="stretch">
        <Col xs={24} lg={12}>
          {loaded && failed?.count === 0 ? (
            <InsightCard
              tone="success"
              headline="No failed renewals"
              tooltip="Card renewals that failed. Members lost access; nothing retries the payment automatically."
            />
          ) : (
            <InsightCard
              tone="danger"
              isLoading={isLoading}
              headline={`${formatInteger(failed?.count) ?? '--'} failed renewals not recovered`}
              tooltip="Card renewals that failed. Members lost access; nothing retries the payment automatically."
              detail={join(
                hasAmount(failed?.amount) &&
                  `${money(failed?.amount)} lost per year`,
                cardFailureRate !== null &&
                  `${formatPercent(cardFailureRate)} of card renewals failed in the last 90 days`
              )}
              linkLabel="See failed renewals"
              linkTo={link('/membership/revenue/renewals', {
                outcome: 'payment_failed',
                startDate: failedFrom,
                endDate: today,
              })}
            />
          )}
        </Col>
        <Col xs={24} lg={12}>
          {loaded && stuck?.count === 0 ? (
            <InsightCard
              tone="success"
              headline="No requests waiting"
              tooltip="Paid requests waiting for a staff review before activation."
            />
          ) : (
            <InsightCard
              tone="info"
              isLoading={isLoading}
              headline={`${formatInteger(stuck?.count) ?? '--'} requests waiting for review`}
              tooltip="Paid requests waiting for a staff review before activation."
              detail={
                typeof stuck?.oldestDays === 'number'
                  ? `Oldest waiting ${stuck.oldestDays} ${stuck.oldestDays === 1 ? 'day' : 'days'}`
                  : undefined
              }
              linkLabel="See requests"
              linkTo="/membership/requests"
            />
          )}
        </Col>
        <Col xs={24} lg={12}>
          {loaded && missing?.count === 0 ? (
            <InsightCard
              tone="success"
              headline="Every renewal has a saved card"
              tooltip="Card renewals due in the next 30 days with no card saved at Adyen: they will fail unless the member adds one."
            />
          ) : (
            <InsightCard
              tone="warning"
              isLoading={isLoading}
              // `null` count: Adyen could not be queried. Never "0".
              headline={
                loaded && missing?.count === null
                  ? 'Renewals without a saved card'
                  : `${formatInteger(missing?.count) ?? '--'} renewals without a saved card`
              }
              tooltip="Card renewals due in the next 30 days with no card saved at Adyen: they will fail unless the member adds one."
              detail={
                loaded && missing?.count === null
                  ? 'Could not check with Adyen'
                  : join(
                      hasAmount(missing?.amount) &&
                        `${money(missing?.amount)} due in the next 30 days`,
                      (missing?.unchecked ?? 0) > 0 &&
                        `${missing?.unchecked} could not be checked with Adyen`
                    )
              }
              linkLabel="See these renewals"
              linkTo={link('/membership/revenue/renewals', {
                paymentMethod: 'missing',
                startDate: today,
                endDate: in30,
              })}
            />
          )}
        </Col>
        <Col xs={24} lg={12}>
          {loaded && dormant?.count === 0 ? (
            <InsightCard
              tone="success"
              headline="No dormant member renewing soon"
              tooltip="Renewals due in the next 30 days whose members neither booked nor attended an event in the last 90 days."
            />
          ) : (
            <InsightCard
              tone="purple"
              isLoading={isLoading}
              headline={`${formatInteger(dormant?.count) ?? '--'} dormant members renewing soon`}
              tooltip="Renewals due in the next 30 days whose members neither booked nor attended an event in the last 90 days."
              detail={join(
                hasAmount(dormant?.amount) &&
                  `${money(dormant?.amount)} renewing in the next 30 days`,
                'no reservation or event check-in in the last 90 days'
              )}
              linkLabel="See these renewals"
              linkTo={link('/membership/revenue/renewals', {
                dormant: '1',
                outcome: 'upcoming',
                startDate: today,
                endDate: in30,
              })}
            />
          )}
        </Col>
      </Row>
      {error && <Text type="danger">{error}</Text>}
    </Flex>
  )
}

export default NeedsAttention
